import React from 'react';
import { useReportRedirect } from '@/reports/index';
import { PropertyTable } from '@/reports/property/sections/PropertyTable';
import {
  LizanoListInfo,
  LizanoListProperty,
} from '@/reports/sections/ListsSection/lists/lists';
import { Button, styled } from '@/theme/index';
import { ValidReportTypes } from '@/utils/helperTypes';
import { commaFormatCurrency, formatPhone, DateUtil } from '@/utils/index';
import { tableStyle, tableColumns } from './utils';

interface ListItemPropertyReportsTableProps {
  listProperties: LizanoListInfo['list_properties'];
  selectedRows: number[];
  setSelectedRows: (selectedRowId: number[]) => void;
  showRapids: boolean;
  toggleShowRapids: () => void;
  viewType: string;
  listCode: string;
  rapidsRef: React.RefObject<HTMLDivElement>;
}

const StyledReportTitleButton = styled(Button)(() => ({
  fontSize: '1rem',
  fontWeight: 'normal',
  textDecorationLine: 'underline',
  padding: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

export const ListItemPropertyReportsTable = ({
  listProperties,
  selectedRows,
  setSelectedRows,
}: ListItemPropertyReportsTableProps) => {
  const { redirectToReportUrl } = useReportRedirect();
  const {
    ADDRESS,
    UNIT,
    CITY,
    STATE,
    ZIP,
    COUNTY,
    APN,
    OWNER_OCCUPIED,
    OWNER_FIRSTNAME,
    OWNER_LASTNAME,
    OWNER_MAILING_ADDRESS,
    OWNER_PHONE,
    OWNER_EMAIL,
    PROPERTY_TYPE,
    BED,
    BATHS,
    LOT_SIZE,
    SQFT,
    EST_VALUE,
    ASSESSED_VALUE,
    LAND_VALUE,
    IMPROVEMENT_VALUE,
    LAST_SOLD_DATE,
    LAST_SOLD_PRICE,
    CREATED,
    UPDATED,
  } = tableColumns;

  const openPropertyReportRedirect = (
    reportType: ValidReportTypes,
    id: string
  ) => {
    redirectToReportUrl(reportType, id);
  };

  const getStreetCell = ({
    street,
    permalink,
  }: LizanoListProperty): JSX.Element | string => {
    if (street) {
      if (permalink) {
        return (
          <StyledReportTitleButton
            variant="text"
            color="primary"
            onClick={() => openPropertyReportRedirect('property', permalink)}
          >
            {street}
          </StyledReportTitleButton>
        );
      }

      return street;
    }

    return '-';
  };

  const tableHeaders = [
    { id: ADDRESS, label: 'Street Address' },
    { id: UNIT, label: 'Unit#' },
    { id: CITY, label: 'City' },
    { id: STATE, label: 'State' },
    { id: ZIP, label: 'Zip' },
    { id: COUNTY, label: 'County' },
    { id: APN, label: 'APN' },
    { id: OWNER_OCCUPIED, label: 'Owner Occupied' },
    { id: OWNER_FIRSTNAME, label: 'Owner First Name' },
    { id: OWNER_LASTNAME, label: 'Owner Last Name' },
    { id: OWNER_MAILING_ADDRESS, label: 'Owner Mailing Address' },
    { id: OWNER_PHONE, label: 'Owner Phone' },
    { id: OWNER_EMAIL, label: 'Owner Email' },
    { id: PROPERTY_TYPE, label: 'Property Type' },
    { id: BED, label: 'Beds' },
    { id: BATHS, label: 'Baths' },
    { id: LOT_SIZE, label: 'Lot Size' },
    { id: SQFT, label: 'Total SqFt' },
    { id: EST_VALUE, label: 'Est. Value' },
    { id: ASSESSED_VALUE, label: 'Assessed Value' },
    { id: LAND_VALUE, label: 'Land Value' },
    { id: IMPROVEMENT_VALUE, label: 'Improvement Value' },
    { id: LAST_SOLD_DATE, label: 'Last Sold Date' },
    { id: LAST_SOLD_PRICE, label: 'Last Sold Price' },
    { id: CREATED, label: 'Created' },
    { id: UPDATED, label: 'Updated' },
  ];

  const generatePayload = (listProperties: LizanoListProperty) => {
    const dateParser = new DateUtil();

    if (!listProperties) return {};
    const {
      street,
      unit,
      city,
      state,
      zip5,
      county,
      apn,
      owner_occupied,
      property_type,
      beds,
      baths,
      lot_size,
      sqft,
      estimated_value,
      assessed_value,
      land_value,
      improvement_value,
      last_date_sold,
      last_sold_price,
      created_at,
      updated_at,
    } = listProperties;

    const {
      first_name,
      last_name,
      mailing_address,
      list_property_owner_emails,
      list_property_owner_phones,
    } = listProperties?.list_property_owners?.[0] ?? [];
    const phone_number = list_property_owner_phones?.[0]?.phone_number;
    const email = list_property_owner_emails?.[0]?.email;

    return {
      [ADDRESS]: {
        accessor: street || '',
        displayData: getStreetCell(listProperties),
      },
      [UNIT]: {
        accessor: unit || '',
        displayData: unit || '-',
      },
      [CITY]: {
        accessor: city || '',
        displayData: city || '-',
      },
      [STATE]: {
        accessor: state || '',
        displayData: state || '-',
      },
      [ZIP]: {
        accessor: zip5 || 0,
        displayData: zip5 || '-',
      },
      [COUNTY]: {
        accessor: county || '',
        displayData: county || '-',
      },
      [APN]: {
        accessor: apn || '',
        displayData: apn || '-',
      },
      [OWNER_OCCUPIED]: {
        accessor: owner_occupied || '',
        displayData: owner_occupied ? 'Yes' : 'No',
      },
      [OWNER_FIRSTNAME]: {
        accessor: first_name || '',
        displayData: first_name || '-',
      },
      [OWNER_LASTNAME]: {
        accessor: last_name || '',
        displayData: last_name || '-',
      },
      [OWNER_MAILING_ADDRESS]: {
        accessor: mailing_address || '',
        displayData: mailing_address || '-',
      },
      [OWNER_PHONE]: {
        accessor: phone_number || '',
        displayData: phone_number ? formatPhone(phone_number) : '-',
      },
      [OWNER_EMAIL]: {
        accessor: email || '',
        displayData: email || '-',
      },
      [PROPERTY_TYPE]: {
        accessor: property_type || '',
        displayData: property_type || '-',
      },
      [BED]: {
        accessor: beds || 0,
        displayData: beds || '-',
      },
      [BATHS]: {
        accessor: baths || 0,
        displayData: baths || '-',
      },
      [LOT_SIZE]: {
        accessor: lot_size || 0,
        displayData: lot_size || '-',
      },
      [SQFT]: {
        accessor: sqft || 0,
        displayData: sqft || '-',
      },
      [EST_VALUE]: {
        accessor: estimated_value || 0,
        displayData: estimated_value
          ? commaFormatCurrency(estimated_value)
          : '-',
      },
      [ASSESSED_VALUE]: {
        accessor: assessed_value || 0,
        displayData: assessed_value ? commaFormatCurrency(assessed_value) : '-',
      },
      [LAND_VALUE]: {
        accessor: land_value || 0,
        displayData: land_value ? commaFormatCurrency(land_value) : '-',
      },
      [IMPROVEMENT_VALUE]: {
        accessor: improvement_value || 0,
        displayData: improvement_value
          ? commaFormatCurrency(improvement_value)
          : '-',
      },
      [LAST_SOLD_DATE]: {
        accessor: last_date_sold || '',
        displayData: last_date_sold || '-',
      },
      [LAST_SOLD_PRICE]: {
        accessor: last_sold_price || 0,
        displayData: last_sold_price
          ? commaFormatCurrency(Number(last_sold_price))
          : '-',
      },
      [CREATED]: {
        accessor: created_at || '',
        displayData:
          dateParser.parseDateFromString(created_at, 'L/dd/yyyy') || '-',
      },
      [UPDATED]: {
        accessor: updated_at || '',
        displayData:
          dateParser.parseDateFromString(updated_at, 'L/dd/yyyy') || '-',
      },
    };
  };

  const tableRows = () => {
    if (!listProperties) return [];

    return listProperties.map((listProperty, index) => ({
      id: index,
      payload: generatePayload(listProperty),
    }));
  };

  return (
    <PropertyTable
      headers={tableHeaders}
      rows={tableRows()}
      selectedRows={selectedRows}
      onSelectRow={setSelectedRows}
      config={{ showIndexColumn: true, isSortable: true, isSelectable: true }}
      customStyles={tableStyle}
    />
  );
};
